import { useState, useEffect, useCallback } from 'react';
import { getData, updateData} from '../../services/homeContactServices';
import { Button, Form} from 'react-bootstrap';
import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import { isQuillContentEmpty, cleanQuillHtml } from '../../utils/common';

const modules = {
  toolbar: [
    // Font family and size
    [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],

    // Headings
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    // Text styles
    ['bold', 'italic', 'underline', 'strike'],

    // Subscript / Superscript
    [{ 'script': 'sub' }, { 'script': 'super' }],

    // Blockquote and code block
    ['blockquote', 'code-block'],

    // Color and background
    [{ 'color': [] }, { 'background': [] }],

    // Text alignment and direction (RTL)
    [{ 'align': [] }, { 'direction': 'rtl' }],

    // Ordered / bullet list
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],

    // Indent and outdent
    [{ 'indent': '-1' }, { 'indent': '+1' }],

      ['link'],

    // Clear formatting
    ['clean']
  ]
};

const formats = [
  'font', 'size',
  'header',
  'bold', 'italic', 'underline', 'strike',
  'script',
  'blockquote', 'code-block',
  'color', 'background',
  'align', 'direction',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];

const Homecontact = () => {
  const [formData, setFormData] = useState({
    id: null,
    heading_one: '',
    conference_location: '',
    heading_three: '',
    hotel_information: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch data and append values
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData();
      if (response && response.length > 0) {
        const existingData = response[0];

        setFormData((prev) => ({
          ...prev,
          id: existingData.id,
          heading_one: existingData.heading_one, 
          conference_location: existingData.conference_location,
          heading_three: existingData.heading_three, 
          hotel_information: existingData.hotel_information, 
        }));
      } else {
        console.warn('No data received from API.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
       setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.heading_one) newErrors.heading_one = "Heading One is required";
    if (!formData.conference_location) newErrors.conference_location = "Heading One info is required";
    if (!formData.heading_three) newErrors.heading_three = "Heading Two  is required";
    if (isQuillContentEmpty(formData.hotel_information)) newErrors.hotel_information = "Heading Two info is required";
    
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };
  // Handle input changes
  const handleChange = (e) => {
    try {
    const { name, value } = e.target;
    let updatedData = { ...formData };
    let updatedErrors = { ...errors };
    updatedData[name] = value;
        if (value) {
        updatedErrors[name] = ''; // Clear error if corrected
      }
      setFormData(updatedData);
      setErrors(updatedErrors);
    }
    catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  // Handle change for Quill editor
  const handleDescriptionChange = (value) => {
    setErrors((prev) => ({ ...prev, hotel_information: isQuillContentEmpty(value) ? 'Heading Two info is required' : '' }));
    setFormData((prev) => ({ ...prev, hotel_information: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        return
      }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        hotel_information: cleanQuillHtml(formData.hotel_information),
      };
      const response = await updateData(payload);
      if (response.status === 200) {
        fetchData(); // Refresh data after update
        toast.success("Updated successfully!", {
                                position: 'top-right',
                              });
      } else {
        toast.error(response?.message ||"Failed to update data. Please try again.", {
                                position: 'top-right',
                              });
      }
    } catch (error) {
      console.error('Error updating data:', error);
     
    } 
    finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
  };

  useEffect(() => {
      const tooltipMap = {
        'ql-bold': 'Bold',
        'ql-italic': 'Italic',
        'ql-underline': 'Underline',
        'ql-strike': 'Strikethrough',
        'ql-script[value="sub"]': 'Subscript',
        'ql-script[value="super"]': 'Superscript',
        'ql-blockquote': 'Blockquote',
        'ql-code-block': 'Code Block',
        'ql-color': 'Text Color',
        'ql-background': 'Background Color',
        'ql-align': 'Align',
        'ql-direction': 'Text Direction',
        'ql-list[value="ordered"]': 'Ordered List',
        'ql-list[value="bullet"]': 'Bullet List',
        'ql-indent[value="-1"]': 'Outdent',
        'ql-indent[value="+1"]': 'Indent',
        'ql-link': 'Insert Link',
        'ql-clean': 'Clear Formatting',
      };

    const setTooltips = () => {
      Object.entries(tooltipMap).forEach(([selector, title]) => {
        const el = document.querySelector(`.ql-toolbar .ql-formats .${selector}`);
        if (el && !el.title) {
          el.setAttribute('title', title);
        }
      });
    };

    const observer = new MutationObserver((mutations, obs) => {
      const toolbarReady = document.querySelector('.ql-toolbar');
      if (toolbarReady) {
        setTooltips();
        obs.disconnect(); // Stop observing once done
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

  return () => observer.disconnect() // Clean up on unmount
  }, []);

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
          <Spinner />
        </div>
      ) :(
        <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
              <p className='mb-3 fs-4'>Event Pages / Conference <b>/ Venue Details</b></p>
            </div>
          </div>
            <Form onSubmit={handleSubmit}>
              <div className='row'>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label className='form-label-custom'>Heading One <small className='text-danger'> * </small></Form.Label>
                <Form.Control
                  type="text"
                  name="heading_one"
                  value={formData.heading_one}
                  onChange={handleChange}
                  isInvalid={!!errors.heading_one}
                />
                <Form.Control.Feedback type="invalid">{errors.heading_one}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="col-md-6 mb-3">
                <Form.Label className='form-label-custom'>Heading One info <small className='text-danger'> * </small></Form.Label>
                <Form.Control
                  type="text"
                  name="conference_location"
                  value={formData.conference_location}
                  onChange={handleChange}
                  isInvalid={!!errors.conference_location}
                />
                <Form.Control.Feedback type="invalid">{errors.conference_location}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className='form-label-custom'>Heading Two <small className='text-danger'> * </small></Form.Label>
                <Form.Control
                  type="text"
                  name="heading_three"
                  value={formData.heading_three}
                  onChange={handleChange}
                  isInvalid={!!errors.heading_three}
                />
                <Form.Control.Feedback type="invalid">{errors.heading_three}</Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label className='form-label-custom'>Heading Two info<small className='text-danger'> * </small></Form.Label>
                <div>
                <ReactQuill
                  value={formData.hotel_information}
                  onChange={handleDescriptionChange}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  placeholder="Enter description"
                  className={`${errors.hotel_information ? 'border border border-1 border-danger rounded' : ''}`} 
                />
                {errors.hotel_information && (
                    <div className="invalid-feedback d-block">
                      {errors.hotel_information}
                    </div>
                  )}
                  </div>
              </Form.Group>
              </div>
              <Button variant="success" type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Submit'}
              </Button>
            </Form>
        
        </div>
        <Footer />
         </div>
        )}
      </div>
    </div>
  );
};

export default Homecontact;
